import { useApi } from "@/hooks/use-api";
import { DocumentInfo } from "@/types/document";
import {
  MoreVertical,
  Archive,
  Trash2,
  ShieldAlert,
  Download,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { FeedbackFormatDialog } from "./FeedbackFormatDialog";
import { useState } from "react";

interface DocumentActionsProps {
  doc: DocumentInfo;
  onFlagChange: (e: React.MouseEvent) => void;
}

export const DocumentActions = ({
  doc,
  onFlagChange,
}: DocumentActionsProps) => {
  const { updateDocument, deleteDocument, exportFeedback, exportAuditReport } = useApi();
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const isGraded = !!doc.grade;

  const handleStatusChange = async (
    e: React.MouseEvent,
    newStatus: "flagged" | "ready" | "archived"
  ) => {
    e.stopPropagation();
    await updateDocument(doc.id, { status: newStatus });
  };

  const handleDeleteDocument = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteDocument(doc.id);
  };

  const handleDownloadAuditReport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (doc.assessmentId) {
      try {
        await exportAuditReport(doc.assessmentId, doc.id);
      } catch (err) {
        console.error(err);
        toast.error("Failed to download audit report");
      }
    }
  };

  const handleDownloadFeedback = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setFeedbackDialogOpen(true);
  };

  // Don't render dropdown for importing documents
  if (doc.status === "importing") {
    return null;
  }

  return (
    <>
      <FeedbackFormatDialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        documentId={doc.id}
        exportFeedback={exportFeedback}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-6 w-6">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {doc.status !== "archived" && doc.status !== "failed" && (
            <>
              <DropdownMenuItem onClick={onFlagChange}>
                <ShieldAlert className="mr-2 h-4 w-4" />
                {doc.status === "flagged" ? "Remove flag" : "Flag submission"}
              </DropdownMenuItem>
              {isGraded && (
                <>
                  <DropdownMenuItem onClick={handleDownloadFeedback}>
                    <Download className="mr-2 h-4 w-4" />
                    Download feedback
                  </DropdownMenuItem>
                  {doc.assessmentId && (
                    <DropdownMenuItem onClick={handleDownloadAuditReport}>
                      <FileText className="mr-2 h-4 w-4" />
                      Download audit report
                    </DropdownMenuItem>
                  )}
                </>
              )}
              <DropdownMenuItem
                onClick={(e) => handleStatusChange(e, "archived")}
              >
                <Archive className="mr-2 h-4 w-4" />
                Archive submission
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem
            onClick={handleDeleteDocument}
            className="text-red-600 focus:text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4 text-red-600" />
            Delete submission
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
